import { AutoComplete } from 'primereact/autocomplete';
import { classNames } from 'primereact/utils';
import { Control, FieldPath, useController } from 'react-hook-form';
import { ReactElement, useEffect } from 'react';
import ErrorLabel from '../ErrorLabel';
import { CountryOrStateValues } from './CountryAndStateDropdownField';

export interface StateDropdownFieldProps<TFieldValues> {
  control: Control<TFieldValues>;
  selectedCountryCode: string | undefined;
}

export default function CountryDropdown<TFieldValues>(props: any) {
  // *states from parent component since stateDropdown needs access to selectedCountry:
  const {
    control,
    countries,
    selectedCountry,
    setSelectedCountry,
    setCountries,
    setLocalCountry,
    localCountry,
    countriesData,
    virtualItemsOnly,
  } = props;

  // hooks
  const {
    field,
    fieldState,
    formState: { errors },
  } = useController({
    name: 'country' as FieldPath<TFieldValues>,
    control,
    rules: countries
      ? {
          // If there are states in this country, it's required:
          required: 'Country is required.',
          // and it must be a valid state:
          validate: {
            invalidStateCode: v =>
              isValidStateCode(v as string | FieldPath<TFieldValues>),
          },
        }
      : {},
  });

  // * Helps with browser autofill
  useEffect(() => {
    const countryInput = document.getElementById('country')?.firstElementChild;
    if (countryInput) {
      countryInput.setAttribute('autocomplete', 'country-name');
    }
  }, []);

  const countryOptionTemplate = ({
    name,
    code,
  }: CountryOrStateValues): ReactElement => (
    <div className="flex align-items-center">
      <img
        alt={name}
        src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${code.toLocaleUpperCase()}.svg`}
        width={25}
        className={`flag flag-${code.toLowerCase()} mr-3`}
      />
      <div className="mr-2">{name}</div>
      <div>({code})</div>
    </div>
  );

  const isValidStateCode = (
    countryCode: string | Record<string, string>,
  ): boolean =>
    // *validate countryCode if typed or selected
    countriesData.find(
      country =>
        country.code.toLowerCase() ===
        (typeof countryCode === 'string'
          ? countryCode?.toLowerCase()
          : countryCode.code.toLowerCase()),
    ) !== undefined;

  const searchCountry = (event: { query: string }, onChange: any): void => {
    const findCountry: CountryOrStateValues[] | undefined = countriesData.find(
      country =>
        country.name.toLowerCase() === event.query.toLowerCase() ||
        country.iso3?.toLowerCase() === event.query.toLowerCase(),
    );
    const filteredCountries = countriesData.filter(
      country =>
        country.name.toLowerCase().includes(event.query.toLowerCase()) ||
        country.iso3?.toLowerCase().includes(event.query.toLowerCase()) ||
        country.code.toLowerCase().includes(event.query.toLowerCase()),
    );

    // *SetTimeout gives a chance for the user to type out the country name and show the loading spinner:
    setTimeout(() => {
      if (findCountry) {
        onChange(findCountry);
        setSelectedCountry(findCountry);
        setCountries([]);
      }

      if (findCountry && filteredCountries.length === 1) {
        setCountries([]);
      } else if (
        filteredCountries.length > 1 ||
        filteredCountries.length === 0
      ) {
        const fuzzyMatch = countriesData.filter(
          country =>
            country.name.toLowerCase().includes(event.query.toLowerCase()) ||
            country.iso3?.toLowerCase().includes(event.query.toLowerCase()) ||
            country.code.toLowerCase().includes(event.query.toLowerCase()),
        );
        setCountries(fuzzyMatch);
      } else {
        setCountries(filteredCountries);
      }
    }, 500);
  };

  return (
    <div className="p-field">
      <span className="p-float-label">
        <AutoComplete
          {...field}
          id={field.name}
          inputClassName={`${
            selectedCountry?.code ? 'border-1 border-teal-300' : null
          }`}
          value={selectedCountry || field.value}
          suggestions={countries}
          completeMethod={e => searchCountry(e, field.onChange)}
          field="name"
          dropdown
          autoHighlight
          ref={field.ref}
          style={{ width: '100%' }}
          onChange={e => {
            console.log('field.value', field.value, typeof field.value);
            field.onChange(e.value);
            setSelectedCountry(e.value);
          }}
          onBlur={() => {
            if (!selectedCountry?.code) {
              const country = (countriesData as CountryOrStateValues[]).find(
                country =>
                  country.name.toLowerCase() === localCountry.toLowerCase() ||
                  country.iso3?.toLowerCase() === localCountry.toLowerCase() ||
                  country.code.toLowerCase() === localCountry.toLowerCase(),
              );
              if (country) {
                field.onChange(country);
                setSelectedCountry(country);
              }
            }
            setLocalCountry('');
          }}
          onKeyUp={e => {
            setLocalCountry((e.target as HTMLInputElement).value);
          }}
          itemTemplate={countryOptionTemplate}
          className={classNames({ 'p-invalid': fieldState.invalid })}
          disabled={!virtualItemsOnly}
          readOnly={!virtualItemsOnly}
        />

        <label
          htmlFor="country"
          className={classNames({
            // ? Is there a better way to do this?
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            'p-error': errors.country,
          })}
        >
          Country
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.3rem',
              color: '#14a8a6',
            }}
          >
            *
          </span>
        </label>
      </span>
      <ErrorLabel errors={errors} name="country" />
    </div>
  );
}
